/**
 * Trial Banner Component
 *
 * Displays a prominent banner for users in their reverse trial period.
 * Shows days remaining and encourages upgrade with loss aversion messaging.
 */

import { Link } from 'react-router-dom';
import { Crown, Clock, Sparkles, ArrowRight, X } from 'lucide-react';
import { Button } from '@/client/components/ui/button';
import { useBilling } from '@/client/hooks/use-billing';
import { useState } from 'react';
import { cn } from '@/client/lib/utils';

export function TrialBanner() {
  const { isInReverseTrial, reverseTrialDaysRemaining } = useBilling();
  const [isDismissed, setIsDismissed] = useState(false);

  // Don't show if not in trial or dismissed
  if (!isInReverseTrial || isDismissed) {
    return null;
  }

  const daysRemaining = reverseTrialDaysRemaining ?? 0;

  // Urgency levels based on days remaining
  const isUrgent = daysRemaining <= 3;
  const isWarning = daysRemaining <= 7 && daysRemaining > 3;

  // Dynamic messaging based on urgency
  const getMessage = () => {
    if (daysRemaining === 0) {
      return "Your Pro trial ends today! Don't lose access to premium features.";
    }
    if (daysRemaining === 1) {
      return 'Last day of your Pro trial! Upgrade now to keep your features.';
    }
    if (isUrgent) {
      return `Only ${daysRemaining} days left of your Pro trial. Upgrade to keep all features.`;
    }
    if (isWarning) {
      return `${daysRemaining} days left in your Pro trial. You're experiencing the full power of Armenian Link.`;
    }
    return `You have ${daysRemaining} days of free Pro access. Explore all premium features!`;
  };

  return (
    <div
      className={cn(
        'relative flex items-center justify-between gap-4 px-4 py-3 text-sm transition-colors',
        isUrgent
          ? 'bg-gradient-to-r from-destructive/10 via-destructive/5 to-destructive/10 border-b border-destructive/20'
          : isWarning
            ? 'bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-amber-500/10 border-b border-amber-500/20'
            : 'bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border-b border-primary/20'
      )}
    >
      {/* Left side: Icon + Message */}
      <div className="flex items-center gap-3 flex-1">
        <div
          className={cn(
            'flex items-center justify-center size-8 rounded-lg shrink-0',
            isUrgent
              ? 'bg-destructive/10 text-destructive'
              : isWarning
                ? 'bg-amber-500/10 text-amber-600'
                : 'bg-primary/10 text-primary'
          )}
        >
          {isUrgent ? <Clock className="size-4 animate-pulse" /> : <Crown className="size-4" />}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={cn(
              'font-medium',
              isUrgent ? 'text-destructive' : isWarning ? 'text-amber-700' : 'text-foreground'
            )}
          >
            {getMessage()}
          </span>

          {/* Day counter badge */}
          <span
            className={cn(
              'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold',
              isUrgent
                ? 'bg-destructive/20 text-destructive'
                : isWarning
                  ? 'bg-amber-500/20 text-amber-700'
                  : 'bg-primary/20 text-primary'
            )}
          >
            <Sparkles className="size-3" />
            {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining
          </span>
        </div>
      </div>

      {/* Right side: CTA + Dismiss */}
      <div className="flex items-center gap-2 shrink-0">
        <Button
          asChild
          size="sm"
          variant={isUrgent ? 'destructive' : 'default'}
          className={cn('gap-1.5', !isUrgent && !isWarning && 'bg-primary hover:bg-primary/90')}
        >
          <Link to="/settings/billing">
            Keep Pro Access
            <ArrowRight className="size-3" />
          </Link>
        </Button>

        {/* Dismiss button (only show if not urgent) */}
        {!isUrgent && (
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setIsDismissed(true)}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="size-4" />
            <span className="sr-only">Dismiss</span>
          </Button>
        )}
      </div>
    </div>
  );
}
